import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin, Clock, User, Filter as FilterIcon, X } from "lucide-react";
import { http } from "@/lib/api";
import { CATEGORIES, DAYS, PORTES_OUVERTES_URL, INSCRIPTIONS_URL } from "@/lib/data";
import { PLAN } from "@/constants/testIds";

const AUDIENCES = ["Tous", "enfants", "ados", "adultes"];
const LEVELS = ["Tous", "débutant", "intermédiaire", "avancé", "tous niveaux"];

export default function Planning() {
  const [courses, setCourses] = React.useState([]);
  const [teachers, setTeachers] = React.useState([]);
  const [params, setParams] = useSearchParams();

  const f = {
    city: params.get("city") || "all",
    day: params.get("day") || "all",
    category: params.get("category") || "all",
    audience: params.get("audience") || "Tous",
    level: params.get("level") || "Tous",
    teacher: params.get("teacher") || "all",
  };

  const set = (k, v) => {
    const p = new URLSearchParams(params);
    if (v === "all" || v === "Tous" || !v) p.delete(k); else p.set(k, v);
    setParams(p, { replace: true });
  };
  const reset = () => setParams(new URLSearchParams(), { replace: true });

  React.useEffect(() => {
    http.get("/courses").then((r) => setCourses(r.data)).catch(() => setCourses([]));
    http.get("/teachers").then((r) => setTeachers(r.data)).catch(() => setTeachers([]));
  }, []);

  const cities = React.useMemo(() => Array.from(new Set(courses.map(c => c.city))), [courses]);
  const filtered = courses.filter(c =>
    (f.city === "all" || c.city === f.city) &&
    (f.day === "all" || c.day === f.day) &&
    (f.category === "all" || c.category === f.category) &&
    (f.audience === "Tous" || c.audience === f.audience) &&
    (f.level === "Tous" || c.level === f.level) &&
    (f.teacher === "all" || (c.teacher || "") === f.teacher)
  );

  const byDay = React.useMemo(() => {
    const map = {};
    for (const c of filtered) { (map[c.day] ||= []).push(c); }
    for (const d of Object.keys(map)) map[d].sort((a, b) => a.start.localeCompare(b.start));
    return DAYS.filter(d => map[d]).map(d => [d, map[d]]);
  }, [filtered]);

  return (
    <div data-testid={PLAN.root} className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="he-overline text-neutral-500">Saison 2026-2027</p>
          <h1 className="font-heading text-5xl sm:text-6xl font-black tracking-tighter mt-3">Planning</h1>
          <p className="mt-3 text-neutral-600 font-body max-w-xl">Filtrez par ville, jour, discipline, âge, niveau ou professeur.</p>
          <a
            href="/docs/planning-2026-2027.pdf"
            target="_blank"
            rel="noreferrer"
            data-testid="planning-pdf-download"
            className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[var(--he-red)] link-underline"
          >
            Télécharger le planning officiel (PDF) ↓
          </a>
        </div>
        <button onClick={reset} data-testid={PLAN.resetFilters} className="text-sm inline-flex items-center gap-1 text-neutral-600 hover:text-[var(--he-red)]">
          <X className="w-4 h-4" /> Réinitialiser
        </button>
      </div>

      {/* Filters */}
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Select testid={PLAN.filterCity} label="Ville" value={f.city} onChange={(v) => set("city", v)} options={[["all", "Toutes"]].concat(cities.map(c => [c, c]))} />
        <Select testid={PLAN.filterDay} label="Jour" value={f.day} onChange={(v) => set("day", v)} options={[["all", "Tous"]].concat(DAYS.map(d => [d, d]))} />
        <Select testid={PLAN.filterCategory} label="Style" value={f.category} onChange={(v) => set("category", v)} options={CATEGORIES.map(c => [c.value, c.label])} />
        <Select testid={PLAN.filterAudience} label="Public" value={f.audience} onChange={(v) => set("audience", v)} options={AUDIENCES.map(a => [a, a === "Tous" ? "Tous publics" : a])} />
        <Select testid={PLAN.filterLevel} label="Niveau" value={f.level} onChange={(v) => set("level", v)} options={LEVELS.map(l => [l, l])} />
        <Select testid={PLAN.filterTeacher} label="Professeur" value={f.teacher} onChange={(v) => set("teacher", v)} options={[["all", "Tous"]].concat(teachers.map(t => [t.first_name, t.first_name]))} />
      </div>

      <div className="mt-4 text-xs text-neutral-500 flex items-center gap-1"><FilterIcon className="w-3 h-3" /> {filtered.length} cours affiché{filtered.length > 1 ? "s" : ""}</div>

      {/* Grid by day */}
      <div className="mt-10 space-y-10">
        {byDay.length === 0 && (
          <div className="p-8 border border-dashed border-black/15 rounded-2xl text-center text-neutral-500">
            Aucun cours ne correspond à ces filtres.
          </div>
        )}
        {byDay.map(([day, list]) => (
          <section key={day}>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight border-b border-black/10 pb-3">{day}</h2>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {list.map((c) => (
                <article key={c.id} data-testid={PLAN.courseCard} className="border border-black/10 rounded-2xl p-5 flex flex-col hover:border-[var(--he-red)] transition-colors overflow-hidden">
                  {c.image ? (
                    <div className="-mx-5 -mt-5 mb-4 aspect-[16/10] overflow-hidden">
                      <img src={c.image} alt={`Affiche ${c.name}`} data-testid="planning-course-image" className="w-full h-full object-cover object-top" />
                    </div>
                  ) : null}
                  <div className="flex items-center justify-between">
                    <span className="he-overline text-neutral-500">{c.category}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full border ${c.status === "open" ? "border-black/20 text-black" : "border-neutral-400 text-neutral-500"}`}>{c.status === "open" ? "Ouvert" : c.status}</span>
                  </div>
                  <h3 className="font-heading text-2xl font-bold tracking-tight mt-2">{c.name}</h3>
                  <div className="mt-3 text-sm text-neutral-600 space-y-1.5 font-body">
                    <p className="flex items-center gap-2"><Clock className="w-4 h-4 text-[var(--he-red)]" /> {c.day} · {c.start} – {c.end}</p>
                    <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[var(--he-red)]" /> {c.city}{c.location ? ` · ${c.location}` : ""}</p>
                    <p className="flex items-center gap-2"><User className="w-4 h-4 text-[var(--he-red)]" /> {c.teacher || "Professeur à préciser"}</p>
                    <p className="text-xs text-neutral-500">Public : {c.audience} · Niveau : {c.level}</p>
                    {c.description ? <p className="text-xs text-neutral-500 pt-2 border-t border-black/5 mt-2">{c.description}</p> : null}
                  </div>
                  <div className="mt-5 flex gap-2">
                    <a href={PORTES_OUVERTES_URL} target="_blank" rel="noreferrer" data-testid={PLAN.courseCta} className="btn-red rounded-full px-4 py-2 text-xs">Faire un essai</a>
                    <a href={INSCRIPTIONS_URL} target="_blank" rel="noreferrer" className="text-xs rounded-full px-4 py-2 border border-black/20 hover:bg-black hover:text-white transition-colors">S’inscrire</a>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="flex flex-col text-xs font-body">
      <span className="he-overline text-neutral-500 mb-1.5">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-black/15 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--he-red)]/40"
      >
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}
